import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Clock, Menu, X, Printer, Star, ChevronDown, ChevronUp, Pill, Heart, Award, ShoppingBag, Users, Home, RefreshCw, Syringe, Shield } from 'lucide-react';
import CookieConsent from './CookieConsent';

export default function FallRiverGuardian() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQuickContact, setShowQuickContact] = useState(false);
  const [specialtyExpanded, setSpecialtyExpanded] = useState(true);
  const [generalExpanded, setGeneralExpanded] = useState(true);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [isOpen, setIsOpen] = useState(false);

  // Check if pharmacy is currently open (Halifax/Atlantic Time)
  useEffect(() => {
    const checkIfOpen = () => {
      const halifaxTime = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Halifax',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        weekday: 'short'
      });
      
      const parts = halifaxTime.formatToParts(new Date());
      const weekday = parts.find(p => p.type === 'weekday').value;
      const hour = parseInt(parts.find(p => p.type === 'hour').value);
      const minute = parseInt(parts.find(p => p.type === 'minute').value);
      const currentTime = hour + minute / 60;

      const dayMap = {
        'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6, 'Sun': 0
      };
      const day = dayMap[weekday];

      let openTime, closeTime;

      if (day >= 1 && day <= 5) {
        openTime = 9;
        closeTime = 19;
      } else if (day === 6) {
        openTime = 10;
        closeTime = 17;
      } else {
        openTime = 12;
        closeTime = 17;
      }

      setIsOpen(currentTime >= openTime && currentTime < closeTime);
    };

    checkIfOpen();
    const interval = setInterval(checkIfOpen, 60000);
    return () => clearInterval(interval);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Show/hide floating contact button on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowQuickContact(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hours = [
    { period: 'Monday to Friday', time: '9:00 am - 7:00 pm' },
    { period: 'Saturday', time: '10:00 am - 5:00 pm' },
    { period: 'Sunday', time: '12:00 pm - 5:00 pm' }
  ];

  const healthcareLinks = [
    {
      category: 'Emergency Services',
      links: [
        { name: 'Call 911', url: 'tel:911', description: 'For life-threatening emergencies' },
        { name: 'NS Health Authority', url: 'https://www.nshealth.ca/', description: 'Main health services info' }
      ]
    },
    {
      category: 'Mental Health Resources',
      links: [
        { name: 'Mental Health & Addictions', url: 'https://www.nshealth.ca/mental-health-and-addictions', description: 'NS Mental Health services' },
        { name: 'Crisis Line: 1-888-429-8167', url: 'tel:1-888-429-8167', description: '24/7 mental health crisis support' },
        { name: 'Kids Help Phone', url: 'https://kidshelpphone.ca/', description: 'Support for youth' }
      ]
    },
    {
      category: 'Walk-in Clinics',
      links: [
        { name: 'Find a Walk-in Clinic', url: 'https://www.nshealth.ca/find-healthcare-professional/walk-clinics', description: 'Locate walk-in clinics near you' },
        { name: 'Virtual Care NS', url: 'https://virtualcarens.ca/', description: 'Online healthcare appointments' }
      ]
    },
    {
      category: 'Finding a Family Doctor',
      links: [
        { name: 'Need a Family Practice Registry', url: 'https://needafamilypracticens.ca/', description: 'Register to find a family doctor' },
        { name: 'Your Health NS', url: 'https://your.healthns.ca/', description: 'Access health records and services' }
      ]
    }
  ];

  const specialtyServices = [
    'Blister / Compliance Packaging',
    'Vaccine: Flu (Book via CanImmunize)',
    'Lyme Prevention Treatment Post-Tick Bite',
    'Shingles/Herpes Zoster: Assessing and Prescribing',
    'Urinary Tract Infections (UTIs)/Bladder Infections: Assessing and Prescribing',
    'Prescribing: Therapeutic/Medication Substitution',
    'Prescribing: Renewals/Refills',
    'Take Home Naloxone Kits Available',
    'Prescribing: Minor Ailments (Pink eye, fungal infections, eczema, allergies, heart burn, 25+ more)',
    'Methadone/Suboxone Services',
    'Basic and Advanced Med Reviews',
    'INR Testing: Community Pharmacist-led Anticoagulant Management Service (CPAMS)',
    'Home Health Care Products and Services',
    'Injections/Immunizations On Site',
   ];

  const generalServices = [
    'Insurance Billing',
    'Certified Compression Stocking Fittings',
    'Loyalty Rewards Cards',
    'Seniors Discount'
  ];

  const pharmacists = [
    { name: 'Shawn Grimm', role: 'Pharmacist / Owner' },
    { name: 'Craig Maclean', role: 'Pharmacist / Owner' },
    { name: 'Michael Wyman', role: 'Pharmacist / Owner' },
    { name: 'Tania Wadden', role: 'Pharmacist' }
  ];

  const technicians = [
    { name: 'Erica King', role: 'Registered Pharmacy Technician' }
  ];

  const technicianCandidates = [
    { name: 'Kayleigh Goucher', role: 'Registered Pharmacy Technician Candidate' }
  ];

  const assistants = [
    'Guida Warford',
    'Jasmine Cranidge'
  ];

  const reviews = [
    {
      author: "Sarah M.",
      text: "The pharmacists here are incredibly knowledgeable and always take time to answer my questions. Best pharmacy experience I've had!",
      rating: 5
    },
    {
      author: "John D.",
      text: "Friendly staff, quick service, and they really care about their customers. Highly recommend!",
      rating: 5
    },
    {
      author: "Lisa K.",
      text: "They went above and beyond to help me with my prescription. Professional and caring team!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-emerald-50">
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Side Menu */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="p-6">
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
          >
            <X size={28} />
          </button>

          <h2 className="text-2xl font-bold text-emerald-800 mb-6 mt-8">Menu</h2>

          {/* Phone */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Contact</h3>
            <a 
              href="tel:9028612020" 
              className="flex items-center gap-3 text-emerald-700 hover:text-emerald-900 p-3 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              <Phone size={20} />
              <span className="font-medium">902-861-2020</span>
            </a>
          </div>

          {/* Hours */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Hours</h3>
            <div className="space-y-2">
              {hours.map((item, index) => (
                <div key={index} className="flex justify-between text-sm p-2">
                  <span className="font-medium text-gray-700">{item.period}:</span>
                  <span className="text-gray-600">{item.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Directions */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Location</h3>
            <a 
              href="https://www.google.com/maps?q=3084+Highway+2,+Fall+River,+NS+B2T+1J5"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-emerald-700 hover:text-emerald-900 p-3 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              <MapPin size={20} />
              <span className="font-medium">Get Directions</span>
            </a>
          </div>

          {/* Healthcare Resources */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Healthcare Resources</h3>
            {healthcareLinks.map((section, idx) => (
              <div key={idx} className="mb-4">
                <h4 className="font-semibold text-emerald-800 mb-2 text-sm">
                  {section.category}
                </h4>
                <div className="space-y-1">
                  {section.links.map((link, linkIdx) => (
                    <a
                      key={linkIdx}
                      href={link.url}
                      target={link.url.startsWith('tel:') ? '_self' : '_blank'}
                      rel={link.url.startsWith('tel:') ? undefined : 'noopener noreferrer'}
                      className="block p-2 rounded-lg hover:bg-emerald-50 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="font-medium text-emerald-700 text-sm">
                        {link.name}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        {link.description}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Quick Contact Button */}
      <a
        href="tel:9028612020"
        className={`fixed bottom-6 right-6 bg-emerald-700 text-white p-4 rounded-full shadow-lg hover:bg-emerald-800 hover:scale-110 transition-all duration-300 z-50 ${
          showQuickContact ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Call pharmacy"
      >
        <Phone size={24} />
      </a>

      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-800 via-emerald-700 to-emerald-800 text-white shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="medical-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 20 8 L 20 32 M 8 20 L 32 20" stroke="white" strokeWidth="2" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#medical-pattern)" />
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              {/* Left side - Guardian logo and Fall River stacked */}
              <div className="flex flex-col">
                <img 
                  src="/images/guardian-logo.png" 
                  alt="Guardian Pharmacy Logo" 
                  className="h-12 sm:h-16 w-auto mb-1"
                />
                <h1 className="text-2xl sm:text-3xl font-bold ml-8">Fall River, Nova Scotia</h1>
              </div>
              
              {/* Right side - Shield icon and tagline */}
              <div className="hidden sm:flex items-center gap-3 ml-4">
                <div className="relative">
                  <Shield className="text-emerald-200" size={40} fill="currentColor" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-emerald-800 font-bold text-lg">+</div>
                  </div>
                </div>
                <p className="text-emerald-100 text-sm animate-fade-in">Feeling better starts here</p>
              </div>
            </div>
            
            {/* Status badge and hamburger menu */}
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                isOpen 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-400 text-white'
              }`}>
                {isOpen ? '● Open Now' : '● Closed Now'}
              </span>
              <button 
                className="text-white hover:text-emerald-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Quick Contact Bar */}
      <div className="bg-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm">
            <div className="flex items-center gap-2">
              <Phone size={16} />
              <a href="tel:9028612020" className="hover:underline">Phone: 902-861-2020</a>
            </div>
            <div className="flex items-center gap-2">
              <Printer size={16} />
              <span>Fax: 902-861-4498</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={16} />
              <span>Address: 3084 Hwy 2, Fall River, NS</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 to-emerald-100 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-emerald-700">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm-1 14h2v2h-2v-2zm0-8h2v6h-2V8z"/>
            </svg>
          </div>
          <div className="absolute top-20 right-20 text-emerald-700">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="8"/>
            </svg>
          </div>
          <div className="absolute bottom-10 left-1/4 text-emerald-700">
            <svg width="70" height="70" viewBox="0 0 24 24" fill="currentColor">
              <rect x="10" y="4" width="4" height="16" rx="2"/>
              <rect x="4" y="10" width="16" height="4" rx="2"/>
            </svg>
          </div>
          <div className="absolute bottom-20 right-1/4 text-emerald-700">
            <svg width="50" height="50" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"/>
            </svg>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-emerald-900 mb-4">
              Your Trusted Community Pharmacy
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Providing exceptional pharmaceutical care and personalized health solutions to the Fall River community since day one.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="tel:9028612020" 
                className="bg-emerald-700 text-white px-8 py-3 rounded-lg hover:bg-emerald-800 transition-all duration-200 hover:scale-105 shadow-md flex items-center gap-2"
              >
                <Phone size={20} />
                Call Us Now
              </a>
              <a 
                href="#map-section" 
                className="bg-white text-emerald-700 px-8 py-3 rounded-lg hover:bg-gray-50 transition-all duration-200 hover:scale-105 shadow-md border-2 border-emerald-700 flex items-center gap-2"
              >
                <MapPin size={20} />
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Banner */}
      <section className="bg-white border-b-4 border-emerald-200 py-8 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center p-4 hover:scale-105 transition-transform duration-200">
              <div className="bg-emerald-100 p-4 rounded-full mb-3">
                <Home className="text-emerald-700" size={36} />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Locally Owned & Operated</h3>
              <p className="text-sm text-gray-600">Part of your community, serving your neighbors</p>
            </div>

            <div className="flex flex-col items-center text-center p-4 hover:scale-105 transition-transform duration-200">
              <div className="bg-yellow-100 p-4 rounded-full mb-3">
                <Award className="text-yellow-600" size={36} />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">32+ Five Star Reviews</h3>
              <p className="text-sm text-gray-600">Trusted by our community for exceptional care</p>
            </div>

            <div className="flex flex-col items-center text-center p-4 hover:scale-105 transition-transform duration-200">
              <div className="bg-emerald-100 p-4 rounded-full mb-3">
                <Users className="text-emerald-700" size={36} />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Expert Pharmacists on Staff</h3>
              <p className="text-sm text-gray-600">Knowledgeable professionals dedicated to your health</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Action Bar */}
      <section className="bg-emerald-700 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-white text-center text-xl font-semibold mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="tel:9028612020"
              className="bg-white rounded-lg p-6 text-center hover:scale-105 hover:shadow-xl transition-all duration-200 group"
            >
              <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-emerald-200 transition-colors">
                <RefreshCw className="text-emerald-700" size={32} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Refill Prescription</h4>
              <p className="text-sm text-gray-600">Call us to refill your prescription</p>
            </a>

            <a
              href="https://novascotia.flow.canimmunize.ca/en/booking/covid-flu-booking"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-6 text-center hover:scale-105 hover:shadow-xl transition-all duration-200 group"
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-200 transition-colors">
                <Syringe className="text-blue-700" size={32} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Book Flu Shot</h4>
              <p className="text-sm text-gray-600">Schedule your flu vaccination</p>
            </a>

            <a
              href="tel:9028612020"
              className="bg-white rounded-lg p-6 text-center hover:scale-105 hover:shadow-xl transition-all duration-200 group"
            >
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-amber-200 transition-colors">
                <Phone className="text-amber-700" size={32} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Contact Us</h4>
              <p className="text-sm text-gray-600">Speak with our pharmacy team</p>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hours Section */}
        <section 
          id="hours-section"
          data-animate
          className={`bg-gradient-to-br from-white to-emerald-50 rounded-lg shadow-md p-6 mb-8 transition-all duration-700 border border-emerald-100 ${
            visibleSections.has('hours-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-2 mb-4">
            <Clock className="text-emerald-700" size={28} />
            <h2 className="text-2xl font-bold text-gray-800">Hours of Operation</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {hours.map((item, index) => (
              <div key={index} className="flex justify-between p-3 bg-gray-200 rounded">
                <span className="font-semibold text-gray-700">{item.period}:</span>
                <span className="text-gray-600">{item.time}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Closed holidays.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Specialty Services */}
          <section 
            id="specialty-services"
            data-animate
            className={`bg-white rounded-lg shadow-md p-6 transition-all duration-700 hover:shadow-xl ${
              visibleSections.has('specialty-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div 
              className="flex justify-between items-center mb-4 cursor-pointer"
              onClick={() => setSpecialtyExpanded(!specialtyExpanded)}
            >
              <div className="flex items-center gap-2">
                <Pill className="text-emerald-700" size={28} />
                <h2 className="text-2xl font-bold text-emerald-800">Specialty Services</h2>
              </div>
              {specialtyExpanded ? <ChevronUp className="text-emerald-700" /> : <ChevronDown className="text-emerald-700" />}
            </div>
            <div className={`overflow-hidden transition-all duration-300 ${specialtyExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <ul className="space-y-2">
                {specialtyServices.map((service, index) => (
                  <li 
                    key={index} 
                    className="flex items-start gap-2 text-gray-700 p-2 rounded hover:bg-emerald-50 transition-colors duration-200"
                  >
                    <span className="text-emerald-600 mt-1">•</span>
                    <span className="text-sm">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* General Services */}
          <section 
            id="general-services"
            data-animate
            className={`bg-white rounded-lg shadow-md p-6 transition-all duration-700 hover:shadow-xl ${
              visibleSections.has('general-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div 
              className="flex justify-between items-center mb-4 cursor-pointer"
              onClick={() => setGeneralExpanded(!generalExpanded)}
            >
              <div className="flex items-center gap-2">
                <ShoppingBag className="text-emerald-700" size={28} />
                <h2 className="text-2xl font-bold text-emerald-800">General Services</h2>
              </div>
              {generalExpanded ? <ChevronUp className="text-emerald-700" /> : <ChevronDown className="text-emerald-700" />}
            </div>
            <div className={`overflow-hidden transition-all duration-300 ${generalExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <ul className="space-y-2 mb-6">
                {generalServices.map((service, index) => (
                  <li 
                    key={index} 
                    className="flex items-start gap-2 text-gray-700 p-2 rounded hover:bg-emerald-50 transition-colors duration-200"
                  >
                    <span className="text-emerald-600 mt-1">•</span>
                    <span className="text-sm">{service}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-gray-700">
                  <strong>Questions?</strong> Call us at (902) 861-2020.
                                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Google Reviews Section */}
        <section 
          id="reviews-section"
          data-animate
          className={`bg-gradient-to-br from-emerald-50 to-white rounded-lg shadow-md p-6 mb-8 transition-all duration-700 border border-emerald-100 ${
            visibleSections.has('reviews-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Award className="text-emerald-700" size={32} />
              <h2 className="text-2xl font-bold text-emerald-800">What Our Customers Say</h2>
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="fill-yellow-400 text-yellow-400" size={24} />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-800">5.0</span>
              <span className="text-gray-600">(33 reviews)</span>
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Guardian+Fall+River+3084+Hwy+2"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-emerald-700 text-white px-6 py-2 rounded-lg hover:bg-emerald-800 transition-colors duration-200"
            >
              Read All Reviews on Google
            </a>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {reviews.map((review, index) => (
              <div 
                key={index} 
                className="p-4 bg-white rounded-lg hover:shadow-md hover:scale-105 transition-all duration-200 border border-emerald-100"
              >
                <div className="flex mb-2">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="fill-yellow-400 text-yellow-400" size={16} />
                  ))}
                </div>
                <p className="text-sm text-gray-700 mb-2 italic">"{review.text}"</p>
                <p className="text-xs text-gray-600 font-semibold">- {review.author}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Map Section */}
        <section 
          id="map-section"
          data-animate
          className={`bg-white rounded-lg shadow-md p-6 mb-8 transition-all duration-700 ${
            visibleSections.has('map-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="text-emerald-700" size={32} />
            <h2 className="text-2xl font-bold text-emerald-800">Find Us</h2>
          </div>
          <div className="mb-4">
            <p className="text-gray-700 flex items-center gap-2">
              <MapPin className="text-emerald-700" size={20} />
              3084 Hwy 2, Fall River, Nova Scotia, B2T 1J5
            </p>
          </div>
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-md">
            <iframe
              title="Guardian Fall River Location"
              src="https://www.google.com/maps?q=3084+Highway+2,+Fall+River,+NS+B2T+1J5&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>

        {/* Staff Section */}
        <section 
          id="staff-section"
          data-animate
          className={`bg-gradient-to-br from-emerald-50 to-white rounded-lg shadow-md p-6 mb-8 transition-all duration-700 border border-emerald-100 ${
            visibleSections.has('staff-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-2 mb-6">
            <Heart className="text-emerald-700" size={32} />
            <h2 className="text-2xl font-bold text-emerald-800">Our Team</h2>
          </div>
          
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Pharmacists</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pharmacists.map((person, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-emerald-50 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  <p className="font-semibold text-gray-800">{person.name}</p>
                  <p className="text-sm text-gray-600">{person.role}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Registered Pharmacy Technicians</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {technicians.map((person, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-emerald-50 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  <p className="font-semibold text-gray-800">{person.name}</p>
                  <p className="text-sm text-gray-600">{person.role}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Registered Pharmacy Technician Candidate</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {technicianCandidates.map((person, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-emerald-50 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  <p className="font-semibold text-gray-800">{person.name}</p>
                  <p className="text-sm text-gray-600">{person.role}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Pharmacy Assistants</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {assistants.map((name, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-gray-50 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  <p className="font-semibold text-gray-800">{name}</p>
                  <p className="text-sm text-gray-600">Pharmacy Assistant</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section 
          id="contact-section"
          data-animate
          className={`bg-emerald-800 text-white rounded-lg shadow-md p-8 text-center transition-all duration-700 ${
            visibleSections.has('contact-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Phone className="text-white" size={32} />
            <h2 className="text-2xl font-bold">Get In Touch</h2>
          </div>
          <div className="space-y-3 max-w-2xl mx-auto">
            <p className="text-lg">
              <strong>Address:</strong> 3084 Hwy 2, Fall River, Nova Scotia, B2T 1J5
            </p>
            <p className="text-lg">
              <strong>Phone:</strong> <a href="tel:9028612020" className="hover:underline">902-861-2020</a>
            </p>
            <p className="text-lg">
              <strong>Fax:</strong> 902-861-4498
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Guardian - Fall River. All rights reserved.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Part of Canada's largest network of locally owned pharmacies
          </p>
        </div>
      </footer>

      {/* Cookie Consent Popup */}
      <CookieConsent />
    </div>
  );
}